/**
 * Week Utilities
 *
 * Utilities for working with ISO week dates and week-based calculations
 * for the planner feature.
 */

/**
 * Get ISO week number for a date
 * @param {Date} date - The date to get the week number for
 * @returns {number} - ISO week number (1-53)
 */
export const getISOWeek = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    const offset = (4 - target.getDay() + 7) % 7;
    target.setMonth(0, 1 + offset);
  }
  return 1 + Math.ceil((firstThursday - target) / 604800000);
};

/**
 * Get ISO year for a date (may differ from calendar year at year boundaries)
 * @param {Date} date - The date to get the ISO year for
 * @returns {number} - ISO year
 */
export const getISOYear = (date) => {
  const target = new Date(date.valueOf());
  const dayOffset = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayOffset + 3);
  return target.getFullYear();
};

/**
 * Format a date as an ISO week string (e.g., '2025-W01')
 * @param {Date} date - The date to format
 * @returns {string} - ISO week string
 */
export const formatISOWeek = (date) => {
  const year = getISOYear(date);
  const week = getISOWeek(date);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Parse an ISO week string to get the first day (Monday) of that week
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {Date} - The Monday of that week
 */
export const parseISOWeek = (weekString) => {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid ISO week format: ${weekString}`);
  }

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  // Get January 4th of the year (always in week 1)
  const jan4 = new Date(year, 0, 4);

  // Get the Monday of week 1
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));

  // Add the number of weeks to get to the target week
  const targetMonday = new Date(mondayOfWeek1);
  targetMonday.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);

  return targetMonday;
};

/**
 * Get the current week as an ISO week string
 * @returns {string} - Current ISO week string
 */
export const getCurrentWeek = () => formatISOWeek(new Date());

/**
 * Compare two ISO week strings
 * @param {string} week1 - First ISO week string
 * @param {string} week2 - Second ISO week string
 * @returns {number} - Negative if week1 < week2, 0 if equal, positive if week1 > week2
 */
export const compareWeeks = (week1, week2) => week1.localeCompare(week2);

/**
 * Add weeks to an ISO week string
 * @param {string} weekString - ISO week string
 * @param {number} weeks - Number of weeks to add (can be negative)
 * @returns {string} - New ISO week string
 */
export const addWeeks = (weekString, weeks) => {
  const monday = parseISOWeek(weekString);
  monday.setDate(monday.getDate() + weeks * 7);
  return formatISOWeek(monday);
};

/**
 * Generate an array of ISO week strings between two weeks (inclusive)
 * @param {string} startWeek - Start ISO week string
 * @param {string} endWeek - End ISO week string
 * @returns {string[]} - Array of ISO week strings
 */
export const getWeeksInRange = (startWeek, endWeek) => {
  const weeks = [];
  let currentWeek = startWeek;

  while (compareWeeks(currentWeek, endWeek) <= 0) {
    weeks.push(currentWeek);
    currentWeek = addWeeks(currentWeek, 1);
  }

  return weeks;
};

/**
 * Get the date range for a week
 * @param {string} weekString - ISO week string
 * @returns {{start: Date, end: Date}} - Object with start (Monday) and end (Sunday) dates
 */
export const getWeekDateRange = (weekString) => {
  const start = parseISOWeek(weekString);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return { start, end };
};

/**
 * Format a week string for display
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @param {string} format - Format type: 'short' (W01) or 'long' (Week 1, 2025)
 * @returns {string} - Formatted week string
 */
export const formatWeekDisplay = (weekString, format = 'short') => {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    return weekString;
  }

  const year = match[1];
  const week = parseInt(match[2], 10);

  if (format === 'short') {
    return `W${week}`;
  }

  if (format === 'medium') {
    // Format: "Sep 25-Oct 6, 2025" (shows date range for the week with year)
    // Falls back to original weekString if date parsing fails
    try {
      const weekRange = getWeekDateRange(weekString);
      const startMonth = weekRange.start.toLocaleString('en-US', {
        month: 'short',
      });
      const startDay = weekRange.start.getDate();
      const endMonth = weekRange.end.toLocaleString('en-US', {
        month: 'short',
      });
      const endDay = weekRange.end.getDate();
      const endYear = weekRange.end.getFullYear();

      if (startMonth === endMonth) {
        // Same month: "Sep 25-30, 2025"
        return `${startMonth} ${startDay}-${endDay}, ${endYear}`;
      }
      // Different months: "Sep 25-Oct 6, 2025"
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${endYear}`;
    } catch {
      return weekString;
    }
  }

  return `Week ${week}, ${year}`;
};

/**
 * Convert ISO week string to date range in YYYY-MM-DD format for API
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {{startDate: string, endDate: string}} - Object with API-format dates
 */
export const weekToDateRange = (weekString) => {
  const { start, end } = getWeekDateRange(weekString);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(start),
    endDate: formatDate(end),
  };
};

/**
 * Convert a YYYY-MM-DD date string to ISO week format
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - ISO week string (e.g., '2025-W01')
 */
export const dateToWeek = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return formatISOWeek(date);
};

/**
 * Get date range for a week range in YYYY-MM-DD format for API queries
 * @param {string} fromWeek - Start ISO week string
 * @param {string} toWeek - End ISO week string
 * @returns {{startDate: string, endDate: string}} - Object with API-format dates
 */
export const getWeekRangeDates = (fromWeek, toWeek) => {
  const { startDate } = weekToDateRange(fromWeek);
  const { endDate } = weekToDateRange(toWeek);

  return { startDate, endDate };
};

/**
 * Format a week as a compact date range (e.g., "Dec 9-15" or "Dec 30-Jan 5")
 * @param {string} weekString - ISO week string (e.g., '2025-W50')
 * @returns {string} - Compact date range string
 */
export const formatWeekDateRangeCompact = (weekString) => {
  try {
    const { start, end } = getWeekDateRange(weekString);
    const startMonth = start.toLocaleString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleString('en-US', { month: 'short' });
    const endDay = end.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}`;
    }
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
  } catch {
    return weekString;
  }
};

/**
 * Get the calendar year from a week string based on the week's start date.
 * This uses the actual start date's year rather than the ISO week year,
 * which provides more intuitive year transitions at year boundaries.
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {number} - The calendar year of the week's start date
 */
export const getYearFromWeek = (weekString) => {
  try {
    const { start } = getWeekDateRange(weekString);
    return start.getFullYear();
  } catch {
    return new Date().getFullYear();
  }
};

/**
 * Format a week's start and end dates separately (e.g., { start: 'Dec 9', end: 'Dec 15' })
 * @param {string} weekString - ISO week string (e.g., '2025-W50')
 * @returns {{start: string, end: string}} - Object with formatted start and end dates
 */
export const formatWeekDatesSeparate = (weekString) => {
  try {
    const { start, end } = getWeekDateRange(weekString);
    const startMonth = start.toLocaleString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleString('en-US', { month: 'short' });
    const endDay = end.getDate();

    return {
      start: `${startMonth} ${startDay}`,
      end: `${endMonth} ${endDay}`,
    };
  } catch {
    return { start: '', end: '' };
  }
};
